import { useState, useRef, useEffect, type CSSProperties } from 'react';
import { createPortal } from 'react-dom';

/**
 * Free text box with a type-ahead list of saved values (sizes, colours,
 * clarities, descriptions…). The value IS the text: anything can be typed,
 * the list is only quick picks. Keyboard: while the list is open, Up / Down
 * move through it and Enter picks; Escape closes the list (not the popup
 * around it). With the list closed, arrow keys go to `onKeyDown`, so an
 * Excel style grid keeps moving between cells. Alt+Down opens the list
 * (plain Down / Up too with `openOnArrow`, for a normal form field).
 */
export function OptionCombobox({
  value, onChange, options, placeholder, className, style, cellRef, onKeyDown: onKeyDownProp,
  panelMinWidth = 160, disabled, title, openOnArrow,
}: {
  openOnArrow?: boolean;
  value: string;
  onChange: (v: string) => void;
  options: string[];
  placeholder?: string;
  className?: string;
  style?: CSSProperties;
  cellRef?: (el: HTMLInputElement | null) => void;
  onKeyDown?: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  panelMinWidth?: number;
  disabled?: boolean;
  title?: string;
}) {
  const [open, setOpen] = useState(false);
  const [hi, setHi] = useState(-1);
  const [pos, setPos] = useState<{ top: number; left: number; width: number; up: boolean } | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const itemRefs = useRef<(HTMLButtonElement | null)[]>([]);

  useEffect(() => {
    if (!open) return;
    function update() {
      const r = inputRef.current?.getBoundingClientRect();
      if (!r) return;
      const up = window.innerHeight - r.bottom < 220 && r.top > 220;
      setPos({ top: up ? r.top - 4 : r.bottom + 4, left: r.left, width: Math.max(r.width, panelMinWidth), up });
    }
    update();
    window.addEventListener('scroll', update, true);
    window.addEventListener('resize', update);
    return () => { window.removeEventListener('scroll', update, true); window.removeEventListener('resize', update); };
  }, [open, panelMinWidth]);

  useEffect(() => {
    if (!open) return;
    function onDoc(e: MouseEvent) {
      const t = e.target as Node;
      if (inputRef.current?.contains(t) || panelRef.current?.contains(t)) return;
      setOpen(false);
    }
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  useEffect(() => { if (hi >= 0) itemRefs.current[hi]?.scrollIntoView({ block: 'nearest' }); }, [hi]);

  // Starts-with matches first, then the rest that contain the text.
  const q = value.trim().toLowerCase();
  const list = !q ? options : [
    ...options.filter(o => o.toLowerCase().startsWith(q)),
    ...options.filter(o => !o.toLowerCase().startsWith(q) && o.toLowerCase().includes(q)),
  ];
  const shown = list.slice(0, 60);
  const showPanel = open && !!pos && shown.length > 0;

  function pick(o: string) {
    onChange(o);
    setOpen(false);
    setHi(-1);
  }

  function handleKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'ArrowDown' && e.altKey) { e.preventDefault(); setOpen(true); return; }
    if (openOnArrow && !showPanel && (e.key === 'ArrowDown' || e.key === 'ArrowUp')) {
      e.preventDefault(); setOpen(true); setHi(0); return;
    }
    if (showPanel) {
      if (e.key === 'ArrowDown') { e.preventDefault(); setHi(h => Math.min(shown.length - 1, h + 1)); return; }
      if (e.key === 'ArrowUp')   { e.preventDefault(); setHi(h => Math.max(0, h - 1)); return; }
      if (e.key === 'Enter') {
        e.preventDefault();
        if (hi >= 0 && shown[hi]) pick(shown[hi]); else setOpen(false);
        return;
      }
      if (e.key === 'Escape') { e.stopPropagation(); setOpen(false); return; }
    }
    if (e.key === 'Tab') setOpen(false);
    onKeyDownProp?.(e);
  }

  return (
    <div style={{ position: 'relative', width: '100%', height: '100%' }}>
      <input
        ref={el => { inputRef.current = el; cellRef?.(el); }}
        className={className}
        style={style}
        value={value}
        placeholder={placeholder}
        disabled={disabled}
        title={title}
        autoComplete="off"
        onChange={e => {
          onChange(e.target.value);
          setOpen(true);
          setHi(e.target.value.trim() ? 0 : -1);
        }}
        onClick={() => { if (options.length > 0) setOpen(true); }}
        onBlur={() => {
          // Typed "g-h" while the list has "G-H": keep the saved spelling.
          const exact = options.find(o => o.toLowerCase() === value.trim().toLowerCase());
          if (exact && exact !== value) onChange(exact);
        }}
        onKeyDown={handleKeyDown}
      />
      {showPanel && createPortal(
        <div ref={panelRef} className="dropdown-panel"
          style={{
            position: 'fixed', left: pos!.left, width: pos!.width, maxHeight: 220, overflowY: 'auto',
            ...(pos!.up ? { bottom: window.innerHeight - pos!.top } : { top: pos!.top }),
          }}>
          {shown.map((o, i) => (
            <button
              key={o} type="button" ref={el => { itemRefs.current[i] = el; }}
              className={`dropdown-item${i === hi ? ' active' : ''}`}
              onMouseDown={e => e.preventDefault()}
              onMouseEnter={() => setHi(i)}
              onClick={() => pick(o)}
              style={{ display: 'block', width: '100%', textAlign: 'left', whiteSpace: 'normal' }}
            >
              {o}
            </button>
          ))}
        </div>,
        document.body,
      )}
    </div>
  );
}
